/**
 * AuthImage — an <img> whose source needs the dashboard's bearer header.
 *
 * The API client fetches the bytes as a blob and the image renders from an
 * object URL (revoked on unmount). Used for previews the anonymous /files
 * routes won't serve: unpublished listing assets and provider logos in the
 * dashboard. Pass `version` to force a refetch after an upload.
 */
import { useEffect, useState } from 'react';
import api from '../services/api';

interface AuthImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  version?: string | number | null;
  fallback?: React.ReactNode;
}

export function AuthImage({ src, version, fallback = null, alt = '', ...rest }: AuthImageProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setObjectUrl(null);
    setFailed(false);
    api.get(src, { responseType: 'blob', baseURL: '' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setObjectUrl(url);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src, version]);

  if (failed) return <>{fallback}</>;
  if (!objectUrl) return <span aria-busy="true" />;
  return <img src={objectUrl} alt={alt} {...rest} />;
}

export default AuthImage;
